import React, { useState } from 'react';

interface Feature {
  id: string;
  title: string;
  description: string;
  icon: string;
  details: string[];
  demo?: {
    type: 'image' | 'video' | 'audio' | 'code';
    content: string;
    caption: string;
  };
}

export const FeatureShowcase: React.FC = () => {
  const [activeFeature, setActiveFeature] = useState('script');

  const features: Feature[] = [
    {
      id: 'script',
      title: 'AI Script Writing',
      description: 'Generate viral scripts using psychological triggers and proven formulas',
      icon: '📝',
      details: [
        'Hook-Value-CTA structure optimized for engagement',
        'Psychological triggers: curiosity, urgency, controversy',
        'Automatic word count and timing calculation',
        'Multi-language support (English, Italian, German)',
        'Viral content patterns from successful creators'
      ],
      demo: {
        type: 'code',
        content: `// Example Generated Script
Hook (5s): "Stop doing productivity wrong in 2024..."
Value (25s): "Here are 3 science-backed methods that actually work:
1. Time-blocking with energy levels
2. The 2-minute rule for small tasks  
3. Batch processing similar activities"
CTA (5s): "Try method #2 today and comment your results!"`,
        caption: 'Real script generated by our AI in 8 seconds'
      }
    },
    {
      id: 'thumbnail',
      title: 'Professional Thumbnails',
      description: 'Eye-catching cover images that stop the scroll and drive clicks',
      icon: '🖼️',
      details: [
        'Multiple AI art models: FLUX.1, SDXL Lightning, Leonardo Phoenix',
        'Brand color integration for consistent visual identity',
        'Style variants: Professional, Bold, Minimalist, Photorealistic',
        'Format optimization: Square, Landscape, Portrait',
        'Advanced prompt engineering for maximum impact'
      ],
      demo: {
        type: 'image',
        content: '/api/assets/demo-thumbnail.jpg',
        caption: 'Generated thumbnail with brand colors and professional style'
      }
    },
    {
      id: 'voice',
      title: 'Studio-Quality Voiceovers',
      description: 'Natural-sounding narration with emotion and perfect pronunciation',
      icon: '🎵',
      details: [
        '12+ professional voice options (male/female)',
        'Deepgram Aura & MeloTTS technology',
        'Automatic speed and tone optimization',
        'Multi-language voice matching',
        'Fallback system ensures 99.9% success rate'
      ],
      demo: {
        type: 'audio',
        content: '/api/assets/demo-voice.mp3',
        caption: 'Sample voiceover generated with Asteria voice'
      }
    },
    {
      id: 'storyboard',
      title: 'Intelligent Shot Planning',
      description: 'Detailed storyboards with visual directions and asset requirements',
      icon: '🎬',
      details: [
        'Shot-by-shot breakdown with precise timing',
        'Visual direction and camera angle suggestions',
        'Asset tags for automatic stock footage matching',
        'Overlay and caption positioning',
        'Industry-standard production format'
      ],
      demo: {
        type: 'code',
        content: `Shot 1 (0-8s): Close-up of modern technology in urban setting
- Assets: ["city", "technology", "modern"]
- Overlay: title-overlay
- Caption: "Stop doing productivity wrong..."

Shot 2 (8-16s): Wide shot of productive workspace
- Assets: ["office", "workspace", "professional"] 
- Overlay: lower-third
- Caption: "Here are 3 science-backed methods..."`,
        caption: 'Automatically generated storyboard with asset matching'
      }
    },
    {
      id: 'assets',
      title: 'Smart Asset Discovery',
      description: 'AI-powered search finds perfect stock footage for every scene',
      icon: '🔍',
      details: [
        'Vectorize semantic search with 1024-dimension embeddings',
        'BGE-large-en-v1.5 model for understanding context',
        'Automatic filtering by style, tags, and relevance',
        'Score-based ranking for best matches',
        'Fallback library of curated stock footage'
      ],
      demo: {
        type: 'code',
        content: `Query: "modern office technology workspace"
Results:
1. "Modern Office Technology" (score: 0.809)
   - Tags: technology, office, business, laptop
   - Style: professional
   - Duration: 15s
   
2. "Creative Workspace Setup" (score: 0.756)
   - Tags: workspace, modern, creative, tech
   - Style: lifestyle`,
        caption: 'Semantic search results with relevance scoring'
      }
    },
    {
      id: 'speed',
      title: 'Lightning Fast Generation',
      description: 'Complete reels in under 5 minutes with intelligent caching',
      icon: '⚡',
      details: [
        'Parallel AI processing for maximum speed',
        'Intelligent caching reduces repeat generation time',
        'Cloudflare edge computing for global performance',
        'Multi-model fallback prevents failures',
        'Real-time progress tracking and status updates'
      ],
      demo: {
        type: 'code',
        content: `Generation Timeline:
💡 Ideas: 5s (cached: 0.2s)
📝 Script: 8s (cached: 0.5s) 
🎬 Storyboard: 12s (cached: 0.8s)
🖼️ Thumbnail: 15s (cached: 0.3s)
🎵 Voiceover: 10s (cached: 0.4s)
━━━━━━━━━━━━━━━━━━━━
Total: ~3 minutes (80% faster with cache)`,
        caption: 'Real performance metrics from production system'
      }
    }
  ];

  const activeFeatureData = features.find(f => f.id === activeFeature) || features[0];

  return (
    <div className="bg-gray-50 py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Powered by Cutting-Edge AI
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            AutoReelr combines multiple AI models and advanced techniques to deliver 
            professional results that rival human-created content.
          </p>
        </div>

        {/* Feature Navigation */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
          {features.map((feature) => (
            <button
              key={feature.id}
              onClick={() => setActiveFeature(feature.id)}
              className={`p-4 rounded-lg text-center transition-all ${
                activeFeature === feature.id
                  ? 'bg-blue-600 text-white shadow-lg scale-105'
                  : 'bg-white text-gray-700 hover:bg-gray-100 shadow-md'
              }`}
            >
              <div className="text-2xl mb-2">{feature.icon}</div>
              <div className="font-semibold text-sm">{feature.title}</div>
            </button>
          ))}
        </div>

        {/* Active Feature Display */}
        <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Left: Feature Details */}
            <div className="p-8 lg:p-12">
              <div className="flex items-center mb-6">
                <span className="text-4xl mr-4">{activeFeatureData.icon}</span>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {activeFeatureData.title}
                  </h3>
                  <p className="text-gray-600">
                    {activeFeatureData.description}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900 text-lg">Key Capabilities:</h4>
                <ul className="space-y-3">
                  {activeFeatureData.details.map((detail, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-green-500 mr-3 mt-1">✓</span>
                      <span className="text-gray-700">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-8">
                <button className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all">
                  Try This Feature
                </button>
              </div>
            </div>

            {/* Right: Demo Content */}
            <div className="bg-gray-900 p-8 lg:p-12 flex items-center">
              <div className="w-full">
                <h4 className="text-yellow-400 font-semibold mb-4">Live Example:</h4>
                
                {activeFeatureData.demo?.type === 'code' && (
                  <div className="bg-black rounded-lg p-4 overflow-x-auto">
                    <pre className="text-green-400 text-sm font-mono whitespace-pre-wrap">
                      {activeFeatureData.demo.content}
                    </pre>
                  </div>
                )}
                
                {activeFeatureData.demo?.type === 'image' && (
                  <div className="text-center">
                    <img 
                      src={activeFeatureData.demo.content}
                      alt="Feature demo"
                      className="max-w-full h-auto rounded-lg shadow-lg mx-auto"
                    />
                  </div>
                )}
                
                {activeFeatureData.demo?.type === 'audio' && (
                  <div className="text-center">
                    <audio controls className="w-full mb-4">
                      <source src={activeFeatureData.demo.content} type="audio/mpeg" />
                      Your browser does not support audio playback.
                    </audio>
                  </div>
                )}
                
                <p className="text-gray-400 text-sm mt-4 italic">
                  {activeFeatureData.demo?.caption}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Technical Stats */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600 mb-2">5min</div>
            <div className="text-gray-600">Average Generation Time</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600 mb-2">90%</div>
            <div className="text-gray-600">Cost Reduction vs Manual</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-600 mb-2">12+</div>
            <div className="text-gray-600">Professional Voice Options</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-600 mb-2">99.9%</div>
            <div className="text-gray-600">Success Rate</div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-12">
          <h3 className="text-3xl font-bold text-white mb-4">
            Ready to Experience the Future of Content Creation?
          </h3>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join thousands of creators who've already transformed their workflow with AutoReelr's AI-powered platform.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-blue-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all">
              🚀 Start Free Trial
            </button>
            <button className="bg-blue-800 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-blue-900 transition-all border border-blue-400">
              📞 Schedule Demo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureShowcase;